/** @jsxImportSource @emotion/react */
import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { Box, Button, useTheme } from '@mui/material'
import { MapLayer, updateMapStateProperty } from '@app/redux/riskMap/riskMapSlice'
import { RootState } from '@app/redux/store'
import { RiskMapView } from '@app/components/Molecules/MapView'
import { SideDrawer } from '@app/components/Molecules/SideDrawer'
import SelectionTool from '@app/components/Pages/Map/MapTools/SelectionTool'
import { ErrorPage } from '@app/routes/ErrorPage'
import { selectedStoryAtom, selectedStoryIdAtom } from '@app/services/storiesService'
import { storiesAtom } from '@app/state/storiesAtom'
import { useAtom, useAtomValue } from 'jotai'
import { UIEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import RiskStoriesDetailLoading from './RiskStoriesDetailLoading'
import { StoryControls } from './RiskStoryControls'
import {
  subNavbar,
  pageContent,
  riskDetailPageContainer,
  sideDrawerContainer,
  storyContainer,
  storyFlexContainer,
  storyScrollingContainer,
  riskDetailMain,
} from './RiskStoryDetail.style'
import { StoryStep } from './RiskStoryStep'
import {
  defaultBranding,
  generateBrandedMediaPinSvg,
  generateSequencedSteps,
  generateStoryLink,
  getCurrentContexts,
  getCurrentElements,
  getCurrentHazards,
  getIndexedLayers,
} from './Stories.utils'
import { ChevronLeft, Share } from '@mui/icons-material'
import { RiskStoryShareDialog } from './RiskStoryShareDialog'
import { clientNameAtom } from '@app/state/clientsAtom'
import { useSnackbars } from '@app/contexts/SnackbarContext'
import { logger } from '@app/utils/logger'
import { appLink } from '@app/config/appLink'
import {
  Branding,
  Location,
  RiskMapSlide,
  SequencedStep,
  Story,
  StoryLocation,
  StoryMedia,
} from '../types'
import mapboxgl from 'mapbox-gl'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import { createRoot } from 'react-dom/client'
import { RiskMapCarousel } from '@app/components/Molecules/MapView/RiskMapView/Tooltip/RiskMapCarousel'
import { isAuthenticatedAtom } from '@app/state/authAtom'

export const RiskStoryDetail = ({ publicView = false }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const mapContext = useMap()
  const navigate = useNavigate()
  const { displaySnackbar } = useSnackbars()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const interactive = publicView ? false : isAuthenticated

  const { storyId } = useParams<{ storyId?: string }>()

  const storiesData = useAtomValue(storiesAtom)

  const [selectedStoryId, setSelectedStoryId] = useAtom(selectedStoryIdAtom)
  if (storyId && selectedStoryId !== Number(storyId)) {
    setSelectedStoryId(Number(storyId))
  }
  const story = useAtomValue(selectedStoryAtom)

  const { flyTo, setBasemap, map, setInteractive, basemap } = useMap()
  const mapRef = useRef(map)

  const {
    regions: regionsData,
    layerData,
    style,
  } = useSelector((state: RootState) => state.riskMap)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [mapRenderedStepIndex, setMapRenderedStepIndex] = useState(0)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [activeMediaCarousel, _setActiveMediaCarousel] = useState<StoryMedia | null>(null)
  const activeMediaCarouselRef = useRef(activeMediaCarousel)
  const setActiveMediaCarousel = (data: StoryMedia | null) => {
    activeMediaCarouselRef.current = data
    _setActiveMediaCarousel(data)
  }

  const [selectedMediaFeature, _setSelectedMediaFeature] =
    useState<mapboxgl.FeatureIdentifier | null>(null)
  const selectedMediaFeatureRef = useRef(selectedMediaFeature)
  const setSelectedMediaFeature = useCallback((data: mapboxgl.FeatureIdentifier | null) => {
    if (selectedMediaFeatureRef.current) {
      mapRef.current?.setFeatureState(selectedMediaFeatureRef.current, { selected: false })
    }
    if (data) {
      mapRef.current?.setFeatureState(data, { selected: true })
    }
    selectedMediaFeatureRef.current = data
    _setSelectedMediaFeature(data)
  }, [])

  const [hoveredMediaFeature, _setHoveredMediaFeature] =
    useState<mapboxgl.FeatureIdentifier | null>(null)
  const hoveredMediaFeatureRef = useRef(hoveredMediaFeature)
  const setHoveredMediaFeature = useCallback((data: mapboxgl.FeatureIdentifier | null) => {
    if (hoveredMediaFeatureRef.current)
      mapRef.current?.setFeatureState(hoveredMediaFeatureRef.current, { hover: false })
    if (data) mapRef.current?.setFeatureState(data, { hover: true })
    hoveredMediaFeatureRef.current = data
    _setHoveredMediaFeature(data)
  }, [])

  const tooltipRef = useRef(
    new mapboxgl.Popup({ closeButton: false, className: 'riskMapCarousel' }),
  )

  // Index the layers
  const { elementsData, scenariosData, contextsData } = useMemo(
    () =>
      layerData
        ? getIndexedLayers(layerData)
        : { elementsData: {}, scenariosData: {}, contextsData: {} },
    [layerData],
  )
  const sequencedSteps = useMemo(() => {
    if (!story || story instanceof Error) return []
    else return generateSequencedSteps(story, elementsData, scenariosData, contextsData)
  }, [story, elementsData, scenariosData, contextsData])

  const deleteSelection = useCallback(() => {
    if (!map) return
    SelectionTool.deleteSelectionArea(map)
  }, [map])

  const { prepareAndUpdateLayers } = useLayers()

  // Update the currently used layers
  const allStoryLayers = useMemo(() => {
    if (!story || story instanceof Error) return {}
    const allElementLayers = story.elements ?? []
    const allContextLayers = story.contexts ?? []
    const allScenarioLayers = story.scenarios ?? []

    for (const chapter of story.chapters) {
      allElementLayers.push(...(chapter.elements ?? []))
      allContextLayers.push(...(chapter.contexts ?? []))
      allScenarioLayers.push(...(chapter.scenarios ?? []))

      for (const step of chapter.steps) {
        allElementLayers.push(...(step.elements ?? []))
        allContextLayers.push(...(step.contexts ?? []))
        allScenarioLayers.push(...(step.scenarios ?? []))
      }
    }

    const reduceLayers = (
      layerIds: string[],
      layerData: {
        [key: string]: MapLayer
      },
    ) =>
      layerIds.reduce(
        (acc, id) =>
          layerData[id]
            ? {
                ...acc,
                [id]: {
                  ...layerData[id],
                },
              }
            : acc,
        {},
      )

    return {
      ...getCurrentElements(reduceLayers(allElementLayers, elementsData)),
      ...getCurrentContexts(reduceLayers(allContextLayers, contextsData)),
      ...getCurrentHazards(reduceLayers(allScenarioLayers, scenariosData)),
    }
  }, [story, elementsData, scenariosData, contextsData])

  const stepsParentRef = useRef<HTMLDivElement>(null)
  const stepsScrollRef = useRef<HTMLDivElement>(null)
  const [isBlockingScrollDetection, setIsBlockingScrollDetection] = useState(false)
  const blockingScrollDetectionTimeout = useRef<NodeJS.Timeout | null>(null)
  const blockScrollDetection = useCallback(() => {
    setIsBlockingScrollDetection(true)
    if (blockingScrollDetectionTimeout.current !== null) {
      clearTimeout(blockingScrollDetectionTimeout.current)
    }
    blockingScrollDetectionTimeout.current = setTimeout(() => {
      setIsBlockingScrollDetection(false)
    }, 1000)
  }, [])
  const updatingMapLayersTimeout = useRef<NodeJS.Timeout | null>(null)

  const addMediaMarkerSourceAndLayer = useCallback(
    (map: mapboxgl.Map, step: SequencedStep) => {
      if (map.getLayer('mediaMarkerLayer')) map.removeLayer('mediaMarkerLayer')
      if (map.getLayer('mediaMarkerLayerCircles')) map.removeLayer('mediaMarkerLayerCircles')
      if (map.getSource('mediaMarkerSource')) map.removeSource('mediaMarkerSource')

      if (step.media && step.media.length > 0) {
        map.addSource('mediaMarkerSource', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: step.media?.map((m, id) => {
              return {
                id: id,
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [m.location.longitude, m.location.latitude],
                },
                properties: {
                  slides: [...m.slides],
                },
              }
            }),
          },
        })

        map.addLayer({
          id: 'mediaMarkerLayerCircles',
          type: 'circle',
          source: 'mediaMarkerSource',
          paint: {
            'circle-stroke-color': (story as Story).branding?.secondary,
            'circle-opacity': 0,
            'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 18, 5],
            'circle-stroke-opacity': [
              'case',
              ['boolean', ['coalesce', ['feature-state', 'selected'], false], true],
              1,
              ['boolean', ['coalesce', ['feature-state', 'hover'], false], true],
              0.5,
              0,
            ],
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3.3, 18, 11.25],
            'circle-pitch-alignment': 'map',
          },
        })

        map.addLayer({
          id: 'mediaMarkerLayer',
          type: 'symbol',
          source: 'mediaMarkerSource',
          layout: {
            'icon-image': 'mediaMarkerIcon',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.33, 18, 1.125],
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
          },
        })
      }
    },
    [story],
  )

  useEffect(() => {
    if (!map || !activeMediaCarouselRef.current || story instanceof Error) return

    const { longitude, latitude } = activeMediaCarouselRef.current.location
    const popup = tooltipRef.current
    popup
      .setOffset({ bottom: [0, -40], top: [0, 5], left: [20, 0], right: [-20, 0] })
      .setMaxWidth('none') // This centers the popup over the pin
      .setLngLat([longitude, latitude])

    // Create a container div
    const popupNode = document.createElement('div')

    // Render React component with full interactivity
    const root = createRoot(popupNode)
    const handleClose = () => {
      popup.remove()
    }
    root.render(
      <RiskMapCarousel
        slides={activeMediaCarouselRef.current.slides}
        branding={story?.branding ?? defaultBranding}
        handleClose={handleClose}
      />,
    )
    popup.setDOMContent(popupNode)

    popup.addTo(map)
    popup.on('close', () => {
      setSelectedMediaFeature(null)
      setActiveMediaCarousel(null)
      root.unmount()
    })
  }, [activeMediaCarousel, map, setSelectedMediaFeature, story])

  const updateLayerVisibilities = useCallback(() => {
    const addMediaMarkers = (step: SequencedStep) => {
      if (!map) return
      addMediaMarkerSourceAndLayer(map, step)
      const mediaCarouselToActivateIndex = step.media?.findIndex((m) => m.activate)
      if (mediaCarouselToActivateIndex !== undefined && mediaCarouselToActivateIndex >= 0) {
        map.once('render', () => {
          if (!step.media) return
          setActiveMediaCarousel(step.media[mediaCarouselToActivateIndex])
          setSelectedMediaFeature({ source: 'mediaMarkerSource', id: mediaCarouselToActivateIndex })
        })
      }
    }

    const step = sequencedSteps[activeStepIndex]
    if (!map || !map.getStyle()) {
      updatingMapLayersTimeout.current = setTimeout(updateLayerVisibilities, 400)
      return
    }
    if (!map) return
    const layerIds = [...(step.elements ?? []), ...(step.contexts ?? []), ...(step.scenarios ?? [])]

    addMediaMarkers(step)
    prepareAndUpdateLayers({
      activeHazardParameters: 'all',
      overwriteHazardParameters: true,
      layers: layerIds.flatMap((id) => (allStoryLayers[id] ? [allStoryLayers[id]] : [])),
      regionMasks:
        step.regions?.flatMap((region) => {
          const regionMask = regionsData.find((r) => r.region === region)
          if (!regionMask) return []
          return [regionMask]
        }) ?? [],
    })
  }, [
    sequencedSteps,
    activeStepIndex,
    map,
    prepareAndUpdateLayers,
    addMediaMarkerSourceAndLayer,
    setSelectedMediaFeature,
    allStoryLayers,
    regionsData,
  ])

  useEffect(() => {
    setInteractive(interactive)
    dispatch(updateMapStateProperty({ canInteractWithLayers: interactive }))
  }, [dispatch, interactive, setInteractive])

  const [initialViewShown, setInitialViewShown] = useState(false)
  const [mediaMarkerLoaded, setMediaMarkerLoaded] = useState(false)

  const loadMediaMarkerIcon = (branding: Branding) => {
    if (!map) return

    const mediaMarkerBitmap = new Image(44, 53)
    mediaMarkerBitmap.onload = () => {
      if (!map.hasImage('mediaMarkerIcon')) {
        map.addImage('mediaMarkerIcon', mediaMarkerBitmap)
        setMediaMarkerLoaded(true)
      }
    }

    mediaMarkerBitmap.src =
      'data:image/svg+xml;base64,' + btoa(generateBrandedMediaPinSvg(branding))

    map.on('click', 'mediaMarkerLayer', (e) => {
      if (!e.features || !map) return
      const clickedFeature = e.features[0]
      if (!clickedFeature || !clickedFeature.properties) return
      const slides = JSON.parse(clickedFeature.properties['slides']) as RiskMapSlide[]
      const [longitude, latitude] = (clickedFeature.geometry as GeoJSON.Point).coordinates
      const location: Location = { longitude, latitude }
      setActiveMediaCarousel({ slides, location })
      if (selectedMediaFeatureRef.current)
        map.setFeatureState(selectedMediaFeatureRef.current, { selected: false })
      map.setFeatureState(clickedFeature, { selected: true })

      setSelectedMediaFeature(clickedFeature)
    })

    map.on('mouseenter', 'mediaMarkerLayer', (e) => {
      map.getCanvas().style.cursor = 'pointer'
      if (!e.features || !map) return
      const hoveredFeature = e.features[0]
      if (!hoveredFeature || !hoveredFeature.properties) return
      setHoveredMediaFeature(hoveredFeature)
    })
    map.on('mouseleave', 'mediaMarkerLayer', () => {
      map.getCanvas().style.cursor = ''
      setHoveredMediaFeature(null)
    })
  }
  // Render the first step on entry
  useEffect(() => {
    if (!story) return
    if (!mediaMarkerLoaded) {
      loadMediaMarkerIcon((story as Story).branding ?? defaultBranding)
      return
    }

    if (initialViewShown) return
    if (!flyTo || !map) return
    if (sequencedSteps.length == 0) return

    updateLayerVisibilities()

    const firstStep = sequencedSteps[0]
    if (firstStep.basemap) {
      // Changing the basemap reinstantiates the map object and halts a flyto
      // So store the flyto destination and execute it once the basemap has changed
      if (firstStep.basemap != basemap) {
        setBasemap(firstStep.basemap)
        setFirstFlyTo(firstStep.location)
      } else {
        flyTo({
          ...firstStep.location,
          usePadding: true,
        })
      }
    }

    setInitialViewShown(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequencedSteps, flyTo, map, mediaMarkerLoaded])

  // OMG! Ok, so whenever the `style` changes a new map is created
  // which means any media markers added have been lost.
  // So reload the media marker icon
  // and readd the media marker layer and source
  const [freshMap, setFreshMap] = useState(false)
  const [firstFlyTo, setFirstFlyTo] = useState<StoryLocation | null | undefined>()

  useEffect(() => {
    setMediaMarkerLoaded(false)
    setFreshMap(true)
  }, [style])

  useEffect(() => {
    if (!freshMap || !map || !mediaMarkerLoaded) return

    const step = sequencedSteps[activeStepIndex]
    if (step.media) {
      addMediaMarkerSourceAndLayer(map, step)
      setSelectedMediaFeature(selectedMediaFeatureRef.current)
    }
    if (firstFlyTo) {
      flyTo({
        ...firstFlyTo,
        usePadding: true,
      })
      setFirstFlyTo(null)
    }
    setFreshMap(false)
  }, [
    activeStepIndex,
    freshMap,
    map,
    sequencedSteps,
    mediaMarkerLoaded,
    firstFlyTo,
    flyTo,
    addMediaMarkerSourceAndLayer,
    setSelectedMediaFeature,
  ])

  useEffect(() => {
    tooltipRef.current.remove()
    if (map) {
      if (map.getLayer('mediaMarkerLayer')) map.removeLayer('mediaMarkerLayer')
      if (map.getLayer('mediaMarkerLayerCircles')) map.removeLayer('mediaMarkerLayerCircles')
      if (map.getSource('mediaMarkerSource')) map.removeSource('mediaMarkerSource')
    }

    if (sequencedSteps.length === 0) return
    const step = sequencedSteps[activeStepIndex]

    if (updatingMapLayersTimeout.current !== null) {
      clearTimeout(updatingMapLayersTimeout.current)
    }

    // Load next layers after a delay to wait for flyTo to finish
    //updatingMapLayersTimeout.current = setTimeout(updateLayerVisibilities, 1800)

    const waitForRenderIfTrue = (
      renderFunc: () => void,
      callback: () => void,
      condition = true,
    ) => {
      if (condition) {
        map?.once('render', () => {
          callback()
        })
        renderFunc()
      } else {
        callback()
      }
    }

    if (step.location && map) {
      map.once('moveend', () => {
        waitForRenderIfTrue(
          () => {
            if (step.basemap) setBasemap(step.basemap)
          },
          () => {
            updateLayerVisibilities()
          },
          !!step.basemap,
        )
      })
      flyTo({
        ...step.location,
        usePadding: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRenderedStepIndex])

  // At random the map loses the media marker so just load if again already!
  useEffect(() => {
    if (map) map.on('styleimagemissing', () => setMediaMarkerLoaded(false))

    mapRef.current = map
  }, [map])

  const changeStep = useCallback(
    (increment: number) => {
      setActiveStepIndex((prev) => {
        // scroll to the new step
        let new_stepIndex = prev
        if ((increment > 0 && prev < sequencedSteps.length - 1) || (increment < 0 && prev > 0)) {
          new_stepIndex = prev + increment
        }

        // Scroll to the new step
        if (stepsParentRef.current && stepsScrollRef.current) {
          const element = stepsParentRef.current.children[new_stepIndex]
          const parent = stepsScrollRef.current
          if (!element || !parent) return prev
          const parentRect = parent.getBoundingClientRect()
          const elementRect = element.getBoundingClientRect()

          // Calculate the new scroll position
          const newScrollTop = parent.scrollTop + elementRect.top - parentRect.top - 20

          // Smoothly scroll to the new position
          blockScrollDetection()
          parent.scrollTo({
            top: newScrollTop,
            behavior: 'smooth',
          })
        }

        return new_stepIndex
      })
      setMapRenderedStepIndex((prev) => {
        if (increment > 0 && prev < sequencedSteps.length - 1) {
          return prev + 1
        } else if (increment < 0 && prev > 0) {
          return prev - 1
        }
        return prev
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sequencedSteps],
  )

  const scrollTimeout = useRef<NodeJS.Timeout | null>(null)

  const onScrollEnd = useCallback(() => {
    setActiveStepIndex((current) => {
      setMapRenderedStepIndex(current)
      return current
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex])

  const onScroll: UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLDivElement

    for (const child_index in stepsParentRef.current?.children || []) {
      const child = stepsParentRef.current?.children[child_index] as HTMLElement
      if (
        Math.max(child.offsetTop, child.offsetTop + child.clientHeight - target.clientHeight / 2) >=
        target.scrollTop
      ) {
        setActiveStepIndex(+child_index)
        break
      }
    }

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current)
    }
    scrollTimeout.current = setTimeout(() => {
      onScrollEnd()
    }, 800)
  }

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        changeStep(-1)
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        changeStep(1)
      }
    },
    [changeStep],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  useEffect(() => {
    deleteSelection()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clientName = useAtomValue(clientNameAtom)

  if (!story && storiesData.isLoading) return <RiskStoriesDetailLoading />
  if (!story && !storiesData.isLoading) return <ErrorPage />

  if (story instanceof Error) {
    if (story.message != 'Not found') return <ErrorPage />
    // Navigate to /view to show not found message
    let path = location.pathname
    if (path.endsWith('/')) path = path.substring(0, path.length - 1)
    const pathBits = path.split('/')
    pathBits.pop()
    return <Navigate to={pathBits.join('/')} />
  }

  const storyLink = generateStoryLink(story, clientName)

  const handleShareClickOpen = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShareDialogOpen(true)
  }
  const handleShareClickClose = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShareDialogOpen(false)
  }

  const handleCopy = (text: string, message: string) => async () => {
    try {
      await navigator.clipboard.writeText(text)
      displaySnackbar({
        message,
        type: 'success',
      })
    } catch (err) {
      logger.error('Failed to copy text: ', err)
    }
  }

  const subNavBarVisible = isAuthenticated && !publicView

  return (
    <Box css={riskDetailMain}>
      {subNavBarVisible && (
        <Box css={subNavbar}>
          <Button onClick={() => navigate(`${appLink.risk.path}/stories`)}>
            <ChevronLeft /> Stories
          </Button>
          <Box style={{ width: '100%' }} />
          <Button onClick={() => navigate(`${appLink.risk.path}/explorer`)} variant="outlined">
            Explore further
          </Button>
          <Button onClick={handleShareClickOpen} startIcon={<Share />} variant="contained">
            Share
          </Button>
        </Box>
      )}

      <Box css={riskDetailPageContainer} id="storyPageContainer">
        <Box css={sideDrawerContainer}>
          <SideDrawer
            isOpen={true}
            mapContext={mapContext}
            side="left"
            initialWidth={600}
            hasToggleArrow={false}
            handleWidthChange={(width: number) => {
              const { left: previousWidth } = mapContext.getMapPadding(true)
              mapContext.setMapPadding({ top: 0, left: width, bottom: 0, right: 0 })
              if (map && width !== previousWidth) {
                flyTo({
                  latitude: map.getCenter().lat,
                  longitude: map.getCenter().lng,
                  zoom: map.getZoom(),
                  usePadding: true,
                  duration: 800,
                })
              }
            }}
          >
            <Box css={pageContent(theme)}>
              <StoryControls
                activeStepIndex={activeStepIndex}
                sequencedSteps={sequencedSteps}
                changeStep={changeStep}
              />
              <Box
                css={storyContainer(theme)}
                onScroll={isBlockingScrollDetection ? undefined : onScroll}
                ref={stepsScrollRef}
              >
                <Box css={storyScrollingContainer()}>
                  <Box css={storyFlexContainer(theme)} ref={stepsParentRef}>
                    {sequencedSteps.map((sequencedStep, index) => (
                      <Box key={index}>
                        <StoryStep
                          sequencedStep={sequencedStep}
                          isActive={activeStepIndex == index}
                          totalSteps={sequencedSteps.length}
                          handleOnClick={() => {
                            changeStep(index - activeStepIndex)
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </SideDrawer>
        </Box>
        <RiskMapView flyToClientLocation={false} />
      </Box>

      {isAuthenticated && (
        <RiskStoryShareDialog
          shareDialogOpen={shareDialogOpen}
          storyLink={storyLink}
          isStoryPublic={story?.visibility == 'Public'}
          onClose={handleShareClickClose}
          onCopy={handleCopy}
        />
      )}
    </Box>
  )
}
